import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Tab,
  Tabs,
} from '@mui/material';
import {
  EmojiEvents as StandingsIcon,
  Schedule as ScheduleIcon,
  Groups as TeamsIcon,
  PersonAdd as SparesIcon,
  LocationOn as VenueIcon,
  HowToReg as DraftIcon,
} from '@mui/icons-material';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { FEATURES, useFeatures } from '../../contexts/FeatureContext';
import unifiedApi from '../../utils/unifiedApi';

const SecondaryNav = () => {
  const location = useLocation();
  const routeParams = useParams();
  const { isAuthenticated } = useAuth();
  const { canViewAnyLeagueDraft, canViewLeagueDraft } = usePermissions();
  const { flags, isEnabled, loaded: featuresLoaded } = useFeatures();
  const [teamLeagueSlug, setTeamLeagueSlug] = useState(null);
  const [resolvedLeague, setResolvedLeague] = useState(null);

  // Resolve league slug from various sources
  const urlLeagueSlugFromPath = location.pathname.match(/\/(?:public\/)?leagues\/([^/]+)/)?.[1];
  const urlTeamSlug = location.pathname.startsWith('/teams/') ? location.pathname.split('/')[2] : null;
  const leagueSlug = routeParams.leagueSlug || urlLeagueSlugFromPath;
  const teamSlug = routeParams.teamSlug || urlTeamSlug;

  const isPublicAccessibleRoute =
    location.pathname === '/' ||
    location.pathname === '/home' ||
    location.pathname.startsWith('/leagues/') ||
    location.pathname.startsWith('/teams/') ||
    location.pathname === '/venues';

  // Fetch league slug from team data when on team detail page
  useEffect(() => {
    const fetchTeamLeague = async () => {
      if (teamSlug && !leagueSlug && isPublicAccessibleRoute) {
        try {
          const teamData = await unifiedApi.getTeamBasic(teamSlug);
          const slug = teamData?.leagueSlug || teamData?.LeagueSlug;
          if (slug) setTeamLeagueSlug(slug);
        } catch (error) {
          console.error('Error fetching team league:', error);
        }
      }
    };
    fetchTeamLeague();
  }, [teamSlug, leagueSlug, isPublicAccessibleRoute]);

  const finalLeagueSlug = leagueSlug || teamLeagueSlug;
  const isOnTeamPage = location.pathname.startsWith('/teams/');
  const isLeagueSlugResolved = !!finalLeagueSlug;
  const hasLeagueContext = isLeagueSlugResolved || isOnTeamPage;
  const hasAnyDraftAccess = isAuthenticated && canViewAnyLeagueDraft();
  // Only look the league up for spares when some league could have them switched off.
  const sparesMayBeOff = !isEnabled(FEATURES.SPARES)
    || (flags?.leagues || []).some((l) => l.overrides?.[FEATURES.SPARES] === false);
  const needsLeagueId = hasAnyDraftAccess || (isAuthenticated && sparesMayBeOff);

  // Draft permissions and feature overrides are keyed by league ID, so resolve the slug only when needed.
  useEffect(() => {
    if (!needsLeagueId || !finalLeagueSlug || location.pathname.startsWith('/admin')) return undefined;
    let cancelled = false;
    const resolveLeague = async () => {
      try {
        const league = await unifiedApi.getLeagueBySlug(finalLeagueSlug);
        if (!cancelled && league?.id) {
          setResolvedLeague({ slug: finalLeagueSlug, id: league.id });
        }
      } catch (error) {
        console.error('Error resolving league for league tabs:', error);
      }
    };
    resolveLeague();
    return () => { cancelled = true; };
  }, [needsLeagueId, finalLeagueSlug, location.pathname]);

  const isLeagueResolved = resolvedLeague?.slug === finalLeagueSlug;
  const showDraftTab = hasAnyDraftAccess
    && isLeagueResolved
    && canViewLeagueDraft(resolvedLeague.id);
  // Wait for the switches so the tab doesn't appear and then vanish in leagues without spares.
  const showSparesTab = isAuthenticated
    && featuresLoaded
    && (!sparesMayBeOff || (isLeagueResolved && isEnabled(FEATURES.SPARES, resolvedLeague.id)));

  // Don't render if no league context or in admin mode
  if (!hasLeagueContext || location.pathname.startsWith('/admin')) return null;

  const tabs = [];

  if (isLeagueSlugResolved) {
    tabs.push(
      { label: 'Standings', path: `/leagues/${finalLeagueSlug}/standings`, icon: <StandingsIcon />, value: 'standings' },
      { label: 'Schedule', path: `/leagues/${finalLeagueSlug}/schedule`, icon: <ScheduleIcon />, value: 'schedule' },
      { label: 'Teams', path: `/leagues/${finalLeagueSlug}/teams`, icon: <TeamsIcon />, value: 'teams' },
    );

    if (showSparesTab) {
      tabs.push({
        label: 'Spares',
        path: `/leagues/${finalLeagueSlug}/spares`,
        icon: <SparesIcon />,
        value: 'spares',
      });
    }

    if (showDraftTab) {
      tabs.push({
        label: 'Draft',
        path: `/leagues/${finalLeagueSlug}/draft`,
        icon: <DraftIcon />,
        value: 'draft',
      });
    }
  }

  tabs.push({
    label: 'Venues',
    path: '/venues',
    icon: <VenueIcon />,
    value: 'venues',
  });

  // Determine active tab
  const getActiveTab = () => {
    const pathname = location.pathname;
    if (pathname.includes('/standings')) return 'standings';
    if (pathname.includes('/schedule')) return 'schedule';
    if (pathname.includes('/spares')) return 'spares';
    if (pathname.includes('/draft')) return 'draft';
    if (pathname.includes('/venues')) return 'venues';
    // Teams tab active for both /leagues/{slug}/teams and /teams/{slug}
    if (pathname.includes('/teams')) return 'teams';
    // Default to standings if on league root
    if (pathname.match(/\/leagues\/[^/]+\/?$/)) return 'standings';
    return false;
  };

  const rawActiveTab = getActiveTab();
  const activeTab = tabs.some(t => t.value === rawActiveTab) ? rawActiveTab : false;

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <Container maxWidth="lg">
        <Tabs
          value={activeTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          textColor="primary"
          indicatorColor="primary"
          aria-label="League navigation"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              px: { xs: 1.5, sm: 2 },
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              icon={tab.icon}
              iconPosition="start"
              component={Link}
              to={tab.path}
            />
          ))}
        </Tabs>
      </Container>
    </Box>
  );
};

export default SecondaryNav;
